import { Stethoscope, Activity } from "lucide-react";
import { formatDoctorName } from "@/lib/doctorName";
import { STATE_BY_KEY, formatMoney, daysBadgeClass } from "./pipelineHelpers";

// Single kanban card. Dense, scannable in a column of 20+.
//
// Layout:
//   row 1:  patient name (bold)         · plan_summary (truncated)
//   row 2:  doctor (small icon)         · days-in-state pill
//   row 3:  total (bold tabular nums)   · status pill
//
// Click is wired in PR 2 (drawer). PR 1 cards are visually clickable
// (cursor + hover state) but do nothing on click yet.
export default function TxPlanCard({ plan, currency = "PHP", onOpen }) {
  const state = STATE_BY_KEY[plan.pipeline_state] ?? STATE_BY_KEY.abandoned;
  const Icon = state.Icon;
  // PR 3 ships plan_code (TP-XXXXXX). Older plans without it fall back to
  // a UUID-derived label so cards never look broken.
  const codePreview = plan.plan_code
    || ("TP-" + (plan.id || "").replace(/-/g, "").slice(0, 6).toUpperCase());
  const summary = plan.plan_summary || "—";
  return (
    <div
      data-testid={`tx-plan-card-${plan.id}`}
      data-pipeline-state={plan.pipeline_state}
      role={onOpen ? "button" : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={() => onOpen?.(plan.id)}
      onKeyDown={(e) => {
        if (!onOpen) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(plan.id);
        }
      }}
      className="group rounded-md border border-slate-200 bg-white p-2.5 shadow-[0_1px_0_rgba(15,23,42,0.04)] hover:border-slate-300 hover:shadow-sm transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-500/40"
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-[13px] font-semibold text-slate-900 truncate">
          {plan.patient_name || "—"}
        </div>
        <div className="text-[10px] tracking-wide text-slate-400 font-mono shrink-0">
          {codePreview}
        </div>
      </div>

      <div className="mt-0.5 text-[11px] text-slate-500 truncate" title={summary}>
        {summary}
      </div>

      <div className="mt-2 flex items-center justify-between gap-2 text-[11px]">
        <div className="flex items-center gap-1 min-w-0 text-slate-500">
          <Stethoscope className="w-3 h-3 shrink-0" strokeWidth={2} />
          <span className="truncate">{formatDoctorName(plan.doctor_name, "—")}</span>
        </div>
        <span
          className={[
            "px-1.5 py-0.5 rounded-md text-[10px] font-medium tabular-nums shrink-0",
            daysBadgeClass(plan.days_in_state, plan.pipeline_state),
          ].join(" ")}
        >
          {plan.days_in_state ?? 0}d
        </span>
      </div>

      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-[14px] font-bold tabular-nums text-slate-900">
          {formatMoney(plan.total_centavos, currency)}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {plan.is_informal ? (
            <span
              className="px-1.5 py-0.5 rounded-md text-[10px] font-medium inline-flex items-center gap-1 bg-teal-100 text-teal-800 ring-1 ring-teal-200"
              title="Ongoing treatment — tracked visit by visit"
            >
              <Activity className="w-3 h-3" strokeWidth={2.5} />
              Ongoing
            </span>
          ) : null}
          <span
            className={[
              "px-1.5 py-0.5 rounded-md text-[10px] font-medium inline-flex items-center gap-1",
              state.pill,
            ].join(" ")}
          >
            <Icon className="w-3 h-3" strokeWidth={2.5} />
            {state.label}
          </span>
        </div>
      </div>

      {plan.pipeline_state === "in_progress" && plan.total_visits > 0 ? (
        <div className="mt-2 text-[10px] text-slate-500 tabular-nums">
          {plan.completed_visits}/{plan.total_visits} visits done
        </div>
      ) : null}
      {plan.open_task_count > 0 ? (
        <div className="mt-2 text-[10px] text-amber-700 font-medium">
          {plan.open_task_count} open task{plan.open_task_count === 1 ? "" : "s"}
        </div>
      ) : null}
    </div>
  );
}
